/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSelector } from "reselect";

import { FeatureGates } from "../../models";
import { ExperimentState } from "../state";

export const getExperimentState = (state: any): ExperimentState => state.experiments;

/**
 * Gets current values for all feature gates.
 * This is to be used where we don't care about source of the feature gate.
 * Note: The ordering here is specific
 * Feature Gates set from env vars, etc have the lowest priority.
 * Then the feature gates from EXP have the next priority.
 * Finally, the feature gates from the URL query string or local overrides have the highest priority.
 */
export const getFeatureGates: (state: any) => FeatureGates = createSelector(
  getExperimentState,
  (experimentState: ExperimentState): FeatureGates => ({
    ...experimentState.environmentFeatureGates,
    ...experimentState.overrideFeatureGates,
  })
);

export const getEnvironmentFeatureGates = (state: any): FeatureGates =>
  getExperimentState(state).environmentFeatureGates;

export const getOverrideFeatureGates = (state: any): FeatureGates =>
  getExperimentState(state).overrideFeatureGates;

export const getFeatureGateValueFromFeatureGates = (
  featureGates: FeatureGates,
  featureGate: string
) => (featureGate ? featureGates[featureGate.toLowerCase()] : undefined);

/**
 * Gets the current value of the feature gate. If the feature gate does not exist,
 * undefined will be returned.
 */
const getFeatureGateValue = (state: any, featureGate: string) =>
  getFeatureGateValueFromFeatureGates(getFeatureGates(state), featureGate);

export const getStringFeatureGateValue = (state: any, featureGate: string): string | undefined => {
  const value = getFeatureGateValue(state, featureGate);

  if (typeof value !== "string") {
    return undefined;
  }

  return value;
};

export const getNumberFeatureGateValue = (state: any, featureGate: string): number | undefined => {
  const value = getFeatureGateValue(state, featureGate);

  if (typeof value !== "number") {
    return undefined;
  }

  return value;
};

export const getBooleanFeatureGateValue = (
  state: any,
  featureGate: string
): boolean | undefined => {
  const value = getFeatureGateValue(state, featureGate);

  if (typeof value !== "boolean") {
    return undefined;
  }

  return value;
};

export const getBooleanFeatureGateEnabled = (state: any, featureGate: string): boolean => {
  const featureGates = getFeatureGates(state);
  return getBooleanFeatureGateEnabledFromFeatureGates(featureGates, featureGate);
};

export const getBooleanFeatureGateEnabledFromFeatureGates = (
  featureGates: FeatureGates,
  featureGate: string
): boolean => getFeatureGateValueFromFeatureGates(featureGates, featureGate) === true; // has to explicitly be true, not truthy
